import React from "react";
import Seo from "@/components/vlyt/Seo";
import { Link, useLocation } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { VLYT } from "@/constants/testIds/vlyt";
import { ArrowUpRight } from "lucide-react";

const RIDER_FAQ = [
  {
    q: "How do I open an account without an SMS code?",
    a: "Vlyt utilizes a No-SIM Authentication protocol. You only need your institutional email and access to campus WiFi to verify your identity and secure a ride.",
  },
  {
    q: "What is the Royal Daily Pass?",
    a: "A recurring daily pass that secures your 8:00 AM pickup at your residence hall. Priority matching within 4 minutes, locked fares with no surge ever, and pricing customized to your unique weekly routine.",
  },
  {
    q: "Can I split a trip with friends?",
    a: "Yes. Multi-stop drop-offs support up to 3 additional stops, and the fare splits automatically per rider.",
  },
  {
    q: "What if my driver is late?",
    a: "If a Royal-tier match doesn't arrive within 4 minutes, the ride is on us and one Royal credit is refunded.",
  },
  {
    q: "How do I cancel my Royal pass?",
    a: "Cancel anytime from the app. No exit fee. Unused rides roll into the next week.",
  },
];

const DRIVER_FAQ = [
  {
    q: "How do I become a Vlyt driver?",
    a: "Apply from /support/drivers with your NIN, driver's license, and vehicle papers. Onboarding takes 2 to 4 days including a road check.",
  },
  {
    q: "How much do drivers keep?",
    a: "Drivers retain 95 percent of every standard ₦200 fare. Vlyt charges a flat ₦10 platform fee to ensure the infrastructure remains a 'marvelous wonder' of efficiency.",
  },
  {
    q: "Do I need to work fixed hours?",
    a: "No, drive when you want. Royal-tier matches require a 4-minute response, so peak demand hours pay best.",
  },
  {
    q: "How and when do I get paid?",
    a: "Fares settle to your linked bank account daily by 8 am. Weekly Royal bonuses land every Sunday.",
  },
];

const TABS = [
  { to: "/support/riders", label: "For Riders" },
  { to: "/support/drivers", label: "For Drivers" },
  { to: "/support/contact", label: "Contact us" },
];

export default function Support({ variant = "riders" }) {
  const location = useLocation();
  const active = location.pathname.includes("drivers")
    ? "drivers"
    : location.pathname.includes("contact")
    ? "contact"
    : "riders";

  const items = variant === "drivers" ? DRIVER_FAQ : RIDER_FAQ;
  const accordionTestId = variant === "drivers" ? VLYT.faq.driverAccordion : VLYT.faq.riderAccordion;

  return (
    <>
      <Seo
        title={`Support · ${variant === "drivers" ? "Drivers" : "Riders"} · Vlyt`}
        canonical={`https://govlyt.com/support/${variant}`}
      />
      <section className="pt-[120px] pb-10 mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="text-eyebrow mb-4">Support Hub</div>
        <h1 className="text-h1 font-display max-w-3xl">
          Answers, faster than a <span className="text-lime">Royal pickup.</span>
        </h1>

        <div className="mt-10 flex flex-wrap gap-2">
          {TABS.map((t) => {
            const isActive =
              (t.to.includes("riders") && active === "riders") ||
              (t.to.includes("drivers") && active === "drivers") ||
              (t.to.includes("contact") && active === "contact");
            return (
              <Link
                key={t.to}
                to={t.to}
                className={`px-4 py-2 rounded-full border text-[13px] transition ${
                  isActive
                    ? "border-lime text-lime bg-[rgba(191,255,0,0.05)]"
                    : "border-[color:var(--vlyt-border)] text-[color:var(--vlyt-muted)] hover:border-[color:var(--vlyt-purple-soft)] hover:text-[color:var(--vlyt-text)]"
                }`}
              >
                {t.label}
              </Link>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 md:px-10 grid lg:grid-cols-[1.6fr_1fr] gap-10">
        <div>
          <Accordion
            type="single"
            collapsible
            className="rounded-[20px] border border-[color:var(--vlyt-border)] bg-[color:var(--vlyt-surface-1)] divide-y divide-[color:var(--vlyt-border)]"
            data-testid={accordionTestId}
          >
            {items.map((item, i) => (
              <AccordionItem key={item.q} value={`item-${i}`} className="border-b-0 px-2">
                <AccordionTrigger className="text-left px-4 py-5 hover:no-underline hover:text-lime text-[16px] font-display">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-5 text-[color:var(--vlyt-muted)] text-[15px] leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <aside className="rounded-[20px] border border-[color:var(--vlyt-border)] bg-[color:var(--vlyt-surface-2)] p-8">
          <div className="text-eyebrow mb-3">Still stuck?</div>
          <h3 className="text-h3 font-display">Talk to a human</h3>
          <p className="text-[color:var(--vlyt-muted)] text-[14px] mt-2">
            Our support team responds within 1 business day, usually much faster.
          </p>
          <Link to="/support/contact" className="btn-lime mt-6">
            Open a ticket <ArrowUpRight size={14} />
          </Link>
        </aside>
      </section>
    </>
  );
}
